import React from 'react';
import { Check } from 'lucide-react';
import { cn } from 'lib/utils';

/** Steps before `current` render done, `current` is highlighted, later steps are muted. */
export interface StepperStep {
  id: string;
  label: React.ReactNode;
}

export interface StepperProps extends React.HTMLAttributes<HTMLOListElement> {
  steps: StepperStep[];
  /** Index of the active step (0-based). */
  current: number;
}

const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  ({ steps, current, className, ...props }, ref) => (
    <ol ref={ref} className={cn('flex items-center', className)} {...props}>
      {steps.map((step, i) => {
        const isDone = i < current;
        const isCurrent = i === current;
        const isLast = i === steps.length - 1;
        return (
          <li key={step.id} className={cn('flex items-center', !isLast && 'flex-1')}>
            <div className="flex items-center gap-2">
              <span
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  isDone && 'bg-success-600 text-white',
                  isCurrent && 'border-2 border-foreground text-foreground',
                  !isDone && !isCurrent && 'border border-border text-muted-foreground',
                )}
              >
                {isDone ? <Check className="h-3.5 w-3.5" aria-hidden /> : i + 1}
              </span>
              <span
                className={cn(
                  'text-sm whitespace-nowrap',
                  isCurrent ? 'font-medium text-foreground' : isDone ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </div>
            {!isLast && (
              <span
                className={cn('mx-3 h-px flex-1', isDone ? 'bg-success-200' : 'bg-border')}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  ),
);

Stepper.displayName = 'Stepper';

export { Stepper };
export default Stepper;
